import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { buildApiUrl } from '../../config/api';
import axios from 'axios';
import ExerciseVideoModal from '../ExerciseVideoModal';
import SessionClock from './SessionClock';
import {
  collectSetsToSave,
  setRowCount,
  setTarget,
  todayLocalISODate,
  formatSessionDate,
  sessionDateKey,
} from './setEntry';
import { repsPrescribed } from '../repScheme';
import { trackingOf, fieldsFor, buildSetRequest, classifyMeasure, describeLoggedSet } from './tracking';
import { keyFor } from './clientId';
import { formatIntensity, formatLoad, loadInputAriaLabel } from '../units';
import { usePreferences } from '../usePreferences';
import { exerciseVideoLink } from '../video';
import '../../styles/train.css';

const Train = () => {
    const { programId, sessionId } = useParams();
    const { token } = useAuth();
    // The unit loads are entered and displayed in, and the bounds in that unit.
    // The client never converts — see units.js.
    const { unit, loadRange } = usePreferences();
    const navigate = useNavigate();
    const [sessionData, setSessionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [performances, setPerformances] = useState({});

    // When this screen was opened, for SessionClock. Once, on mount — a
    // remount (leaving and reopening the session) is honestly a new session's
    // clock. See RestTimer.js for why elapsed time must be timestamp-derived.
    const sessionStartedAt = useRef(Date.now());

    // One key per row, kept until the row is edited or everything saves.
    //
    // After a partial failure the rows stay filled in and Save posts every
    // ready row again, including the ones that saved. Before keys, each of
    // those was recorded a second time. Now the resave repeats their keys and
    // the server answers with the sets it already has (PERF-13). An edited row
    // is a different set and gets a new key, which is what it always was.
    const setKeys = useRef(new Map());
    const rowKey = (exerciseId, setNumber) => `${exerciseId}:${setNumber}`;

    // Which row each saved set came from, by performance id.
    //
    // A key outlives its set if the athlete deletes the set from history while
    // the row is still filled in. The resave would then be a replay, which the
    // server answers 200 WITHOUT recreating a deleted set (PERF-15), and this
    // screen would report "Saved" for a set that is gone. Deleting a set forgets
    // the key of the row that made it, so the resave is a new set.
    const savedFrom = useRef(new Map());
    const forgetSaved = (performanceId) => {
        const row = savedFrom.current.get(performanceId);
        if (row !== undefined) setKeys.current.delete(row);
        savedFrom.current.delete(performanceId);
    };
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showSaveReminder, setShowSaveReminder] = useState(false);
    const [previousPerformances, setPreviousPerformances] = useState({});
    // The demo being watched, or null. This was `selectedVideo` (a bare video
    // id) plus `isModalOpen`, which could disagree — an id with the flag false
    // rendered nothing, and the flag true with no id rendered /embed/undefined.
    const [demo, setDemo] = useState(null);
    // Replaces alert(). { tone: 'success' | 'error' | 'warning', message, details? }
    const [saveResult, setSaveResult] = useState(null);
    // The week of the block being logged. Defaults to 1, which is what every set
    // logged before phases existed is, and what the server stores when a client
    // sends nothing.
    const [weekNumber, setWeekNumber] = useState(1);
    // The block this session belongs to, or null when it belongs to none. A
    // null phase is the normal, permanent state for historic sessions — the week
    // selector simply does not apply to them.
    const [phase, setPhase] = useState(null);

    // Fetch session data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const programResponse = await axios.get(
                    buildApiUrl(`/programs/${programId}`),
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                const session = programResponse.data.sessions?.find(
                    s => s.id === parseInt(sessionId, 10)
                );

                if (!session) {
                    throw new Error('Session not found');
                }

                setSessionData(session);

                // The block, if this session is in one. Fetched separately and
                // never allowed to fail the page: the week selector is an
                // affordance, and a session with no phase logs perfectly well
                // into week 1 without it.
                if (session.phaseId) {
                    try {
                        const phasesResponse = await axios.get(
                            buildApiUrl(`/programs/${programId}/phases`),
                            { headers: { Authorization: `Bearer ${token}` } }
                        );
                        const found = phasesResponse.data.phases?.find(
                            p => p.id === session.phaseId
                        );
                        if (found) setPhase(found);
                    } catch {
                        // Leave phase null; logging still works.
                    }
                }

                setLoading(false);
            } catch (err) {
                setError(err.response?.data?.error || err.message || 'Failed to load session data');
                setLoading(false);
            }
        };

        fetchData();
    }, [programId, sessionId, token]);

    const handleSetLog = (exerciseId, setNumber, field, value) => {
        setKeys.current.delete(rowKey(exerciseId, setNumber));
        setPerformances(prev => ({
            ...prev,
            [exerciseId]: {
                ...(prev[exerciseId] || {}),
                [setNumber]: {
                    ...(prev[exerciseId]?.[setNumber] || {}),
                    [field]: value
                }
            }
        }));
        setHasUnsavedChanges(true);
        setSaveResult(null);
    };

    /**
     * Save every logged set.
     *
     * Set interpretation lives in setEntry.js. The important behaviour here is
     * that nothing is discarded silently: rows that could not be interpreted are
     * reported back, and a partial failure says which sets failed and leaves the
     * unsaved flag set. The previous version dropped rows on a truthiness check
     * and then showed "saved successfully" unconditionally, so a user could log a
     * whole session and have none of it persist without any indication.
     */
    const handleSavePerformances = async () => {
        setIsSaving(true);
        setSaveResult(null);

        const sessionDate = todayLocalISODate();
        // Weight- and reps-tracked rows go through collectSetsToSave, which
        // carries the bodyweight regression fix (a blank load means bodyweight,
        // not "skip"). Rewriting that here would be a second chance to get it
        // wrong, so measured rows are collected alongside it instead.
        const weightTracked = sessionData.exercises.filter(
            (exercise) => !fieldsFor(trackingOf(exercise)).duration
                && !fieldsFor(trackingOf(exercise)).distance
        );
        const { ready, incomplete } = collectSetsToSave(weightTracked, performances, loadRange);

        for (const exercise of sessionData.exercises) {
            const tracking = trackingOf(exercise);
            const fields = fieldsFor(tracking);
            if (!fields.duration && !fields.distance) continue;

            const rows = performances[exercise.id] ?? {};
            for (const setNumber of Object.keys(rows).map(Number).sort((a, b) => a - b)) {
                const field = fields.duration ? 'duration' : 'distance';
                const result = classifyMeasure(rows[setNumber]?.[field], tracking);
                if (result.status === 'empty') continue;
                if (result.status === 'incomplete') {
                    incomplete.push({
                        exerciseId: exercise.id,
                        exerciseName: exercise.exerciseName,
                        setNumber,
                        reason: result.reason,
                    });
                    continue;
                }
                ready.push({
                    exerciseId: exercise.id,
                    exerciseName: exercise.exerciseName,
                    setNumber,
                    tracking,
                    [field]: result.value,
                });
            }
        }

        if (ready.length === 0) {
            setIsSaving(false);
            setSaveResult({
                tone: incomplete.length > 0 ? 'warning' : 'error',
                message: incomplete.length > 0
                    ? 'Nothing saved — every set is missing its reps.'
                    : 'Nothing to save yet. Enter reps for at least one set.',
                details: incomplete.map(
                    s => `${s.exerciseName || `Exercise ${s.exerciseId}`} · set ${s.setNumber}: ${s.reason}`
                )
            });
            return;
        }

        // Settle every request so one rejection does not hide the others.
        const outcomes = await Promise.allSettled(
            ready.map(set =>
                axios.post(
                    buildApiUrl(
                        `/programs/${programId}/sessions/${sessionId}/exercises/${set.exerciseId}/performances`
                    ),
                    buildSetRequest({
                        tracking: set.tracking ?? 'weight',
                        weight: set.weight,
                        reps: set.reps,
                        duration: set.duration,
                        distance: set.distance,
                        unit,
                        setNumber: set.setNumber,
                        clientId: keyFor(setKeys.current, rowKey(set.exerciseId, set.setNumber)),
                        // Which week of the block this is. Independent of the
                        // date: an athlete doing week 2 a fortnight late has a
                        // coherent training week and an incoherent calendar.
                        weekNumber,
                        sessionDate,
                    }),
                    { headers: { Authorization: `Bearer ${token}` } }
                )
            )
        );

        outcomes.forEach((outcome, index) => {
            const id = outcome.status === 'fulfilled' ? outcome.value?.data?.performance?.id : undefined;
            if (id !== undefined) savedFrom.current.set(id, rowKey(ready[index].exerciseId, ready[index].setNumber));
        });

        const failed = outcomes
            .map((outcome, index) => ({ outcome, set: ready[index] }))
            .filter(({ outcome }) => outcome.status === 'rejected')
            .map(({ outcome, set }) => {
                const reason = outcome.reason?.response?.data?.details
                    || outcome.reason?.response?.data?.error
                    || outcome.reason?.message
                    || 'unknown error';
                return `${set.exerciseName || `Exercise ${set.exerciseId}`} · set ${set.setNumber}: ${reason}`;
            });

        const savedCount = ready.length - failed.length;

        if (failed.length === 0) {
            // Only clear the unsaved flag when everything actually persisted.
            setHasUnsavedChanges(false);
            setShowSaveReminder(false);
            setPerformances({});
            setKeys.current.clear();
            savedFrom.current.clear();
        }

        setSaveResult({
            tone: failed.length === 0 ? (incomplete.length > 0 ? 'warning' : 'success') : 'error',
            message: failed.length === 0
                ? `Saved ${savedCount} ${savedCount === 1 ? 'set' : 'sets'}.`
                : `Saved ${savedCount} of ${ready.length} sets. ${failed.length} failed.`,
            details: [
                ...failed,
                ...incomplete.map(
                    s => `Skipped — ${s.exerciseName || `Exercise ${s.exerciseId}`} · set ${s.setNumber}: ${s.reason}`
                )
            ]
        });

        setIsSaving(false);
        await fetchPreviousPerformances();
    };

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
                setShowSaveReminder(true);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    const fetchPreviousPerformances = useCallback(async () => {
        if (!sessionData?.exercises) return;

        try {
            const performancePromises = sessionData.exercises.map(async (exercise) => {
                const response = await axios.get(
                    buildApiUrl(
                        `/programs/${programId}/sessions/${sessionId}/exercises/${exercise.id}/performances`
                    ),
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                // Group by date, keeping set order within each date.
                const performancesByDate = response.data.reduce((acc, perf) => {
                    const date = sessionDateKey(perf.sessionDate);
                    if (!acc[date]) {
                        acc[date] = [];
                    }
                    acc[date].push({
                        // The API returns lowercase `id` from PerformanceResponse.
                        // The uppercase fallback covers a server still returning the
                        // raw gorm.Model, whose ID field marshals capitalised.
                        id: perf.id ?? perf.ID,
                        weight: perf.weight,
                        unit: perf.unit,
                        repsPerformed: perf.repsPerformed,
                        setNumber: perf.setNumber,
                        sessionDate: perf.sessionDate,
                        exerciseEntryId: perf.exerciseEntryId
                    });
                    return acc;
                }, {});

                Object.keys(performancesByDate).forEach(date => {
                    performancesByDate[date].sort((a, b) => a.setNumber - b.setNumber);
                });

                return { exerciseId: exercise.id, performancesByDate };
            });

            const results = await Promise.all(performancePromises);
            setPreviousPerformances(
                results.reduce((acc, { exerciseId, performancesByDate }) => {
                    acc[exerciseId] = performancesByDate;
                    return acc;
                }, {})
            );
        } catch (err) {
            // History is supplementary; a failure here must not block logging.
            setSaveResult(prev => prev ?? {
                tone: 'warning',
                message: 'Could not load previous sessions. Logging still works.'
            });
        }
    }, [programId, sessionId, sessionData, token]);

    useEffect(() => {
        if (sessionData) {
            fetchPreviousPerformances();
        }
    }, [sessionData, fetchPreviousPerformances]);

    // A local video-id parser and its click handler used to live here, with a
    // regex that disagreed with the modal's about the same link. Parsing now
    // lives in components/video.js and there is exactly one of it.
    //
    // Reading the link was consolidated later and for the same reason: this
    // screen read `exercise.youTubeLink` raw, so the "0" placeholder — truthy —
    // rendered a Demo button that opened a modal with no video, on an exercise
    // the library screen showed as having none. exerciseVideoLink is now the
    // only answer to "does this have a demo", on all three screens.

    const handleDeleteDatePerformances = async (exerciseId, date, sets) => {
        const confirmDelete = window.confirm(
            `Delete all ${sets.length} sets logged on ${formatSessionDate(date)}?`
        );
        if (!confirmDelete) return;

        const outcomes = await Promise.allSettled(
            sets
                .map(perf => perf.id ?? perf.ID)
                .filter(Boolean)
                .map(performanceId =>
                    axios.delete(
                        buildApiUrl(
                            `/programs/${programId}/sessions/${sessionId}/exercises/${exerciseId}/performances/${performanceId}`
                        ),
                        { headers: { Authorization: `Bearer ${token}` } }
                    )
                )
        );

        const ids = sets.map(perf => perf.id ?? perf.ID).filter(Boolean);
        outcomes.forEach((outcome, index) => {
            if (outcome.status === 'fulfilled') forgetSaved(ids[index]);
        });

        const failedCount = outcomes.filter(o => o.status === 'rejected').length;
        const deletedCount = outcomes.length - failedCount;

        setSaveResult({
            tone: failedCount === 0 ? 'success' : 'error',
            message: failedCount === 0
                ? `Deleted ${deletedCount} ${deletedCount === 1 ? 'set' : 'sets'}.`
                : `Deleted ${deletedCount} of ${outcomes.length} sets. ${failedCount} failed.`
        });

        await fetchPreviousPerformances();
    };

    const handleDeletePerformance = async (exerciseId, performanceId) => {
        if (!window.confirm('Delete this set?')) return;

        try {
            await axios.delete(
                buildApiUrl(
                    `/programs/${programId}/sessions/${sessionId}/exercises/${exerciseId}/performances/${performanceId}`
                ),
                { headers: { Authorization: `Bearer ${token}` } }
            );
            forgetSaved(performanceId);
            setSaveResult({ tone: 'success', message: 'Set deleted.' });
        } catch (err) {
            setSaveResult({
                tone: 'error',
                message: `Could not delete that set: ${err.response?.data?.error || err.message}`
            });
        } finally {
            await fetchPreviousPerformances();
        }
    };

    if (loading) {
        return (
            <div className="training-session">
                <p className="body-copy training-placeholder">Loading session…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="training-session">
                <div className="status-error training-placeholder">Error: {error}</div>
            </div>
        );
    }

    if (!sessionData) {
        return (
            <div className="training-session">
                <div className="status-error training-placeholder">No session found</div>
            </div>
        );
    }

    const statusClass = {
        success: 'status-success',
        error: 'status-error',
        warning: 'notice-warning'
    }[saveResult?.tone] || 'status-success';

    return (
        <div className="training-session">
            <header className="training-header">
                <div className="training-header-text">
                    <div className="training-header-top">
                        <span className="pill">Training log</span>
                        <SessionClock startedAt={sessionStartedAt.current} />
                    </div>
                    <h1 className="page-title training-title">{sessionData.name}</h1>
                    <p className="meta-copy training-subtitle">
                        Leave the load blank for bodyweight work. Reps are required.
                    </p>

                    {/* Only shown when the session is in a block. A session with
                        no phase has no weeks to choose between, and rendering a
                        selector with one option would be noise. */}
                    {phase && phase.blockLengthWeeks > 1 && (
                        <div className="week-selector">
                            <label htmlFor="week-number">Week of {phase.name}</label>
                            <select
                                id="week-number"
                                value={weekNumber}
                                onChange={(e) => setWeekNumber(Number(e.target.value))}
                            >
                                {Array.from({ length: phase.blockLengthWeeks }, (_, i) => i + 1).map(
                                    (week) => (
                                        <option key={week} value={week}>
                                            Week {week}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                    )}
                </div>
                <div className="header-actions">
                    <button
                        className="btn-primary"
                        onClick={handleSavePerformances}
                        disabled={!hasUnsavedChanges || isSaving}
                    >
                        {isSaving ? 'Saving…' : hasUnsavedChanges ? 'Save session' : 'Saved'}
                    </button>
                    <button
                        onClick={() => navigate(`/programs/${programId}/edit`)}
                        className="btn-ghost"
                    >
                        Back to program
                    </button>
                </div>
            </header>

            {saveResult && (
                <div className={`${statusClass} save-result`} role="status" aria-live="polite">
                    <p className="save-result-message">{saveResult.message}</p>
                    {saveResult.details?.length > 0 && (
                        <ul className="save-result-details">
                            {saveResult.details.map((detail, i) => (
                                <li key={i}>
                                    <span className="list-marker" />
                                    <span>{detail}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {showSaveReminder && hasUnsavedChanges && (
                <div className="callout-panel save-reminder">
                    <p className="save-reminder-text">You have unsaved sets from this session.</p>
                    <button
                        onClick={handleSavePerformances}
                        disabled={isSaving}
                        className="btn-secondary"
                    >
                        {isSaving ? 'Saving…' : 'Save now'}
                    </button>
                </div>
            )}

            <div className="exercise-list">
                {sessionData.exercises.map((exercise) => {
                    const intensity = formatIntensity(exercise);
                    const rows = setRowCount(exercise.sets);
                    // The server resolved this; the client never re-derives the
                    // precedence order, or it would eventually disagree.
                    const tracking = trackingOf(exercise);
                    const fields = fieldsFor(tracking);
                    const history = previousPerformances[exercise.id];
                    const demoLink = exerciseVideoLink(exercise);

                    return (
                        <article className="card exercise-card" key={exercise.id}>
                            <div className="exercise-card-head">
                                <span className="surface-badge series-badge">{exercise.series}</span>
                                <h2 className="section-title exercise-name">{exercise.exerciseName}</h2>
                                {demoLink && (
                                    <button
                                        className="btn-ghost demo-button"
                                        onClick={() => setDemo({
                                            url: demoLink,
                                            name: exercise.exerciseName,
                                        })}
                                    >
                                        Demo
                                    </button>
                                )}
                            </div>

                            <dl className="prescription-grid">
                                <div className="prescription-item">
                                    <dt className="kicker">Sets</dt>
                                    <dd className="mono">{exercise.sets}</dd>
                                </div>
                                <div className="prescription-item">
                                    <dt className="kicker">Reps</dt>
                                    {/* The scheme, not the legacy integer. The set
                                        rows below already label each target from
                                        repScheme; this strip read `reps` and so
                                        announced "8" above rows targeting "8-10". */}
                                    <dd className="mono">{repsPrescribed(exercise)}</dd>
                                </div>
                                <div className="prescription-item">
                                    <dt className="kicker">Tempo</dt>
                                    <dd className="mono">{exercise.tempo || '—'}</dd>
                                </div>
                                <div className="prescription-item">
                                    <dt className="kicker">Rest</dt>
                                    <dd className="mono">{exercise.rest ? `${exercise.rest}s` : '—'}</dd>
                                </div>
                                <div className="prescription-item prescription-item-wide">
                                    <dt className="kicker">Intensity</dt>
                                    <dd className="mono">{intensity || '—'}</dd>
                                </div>
                            </dl>

                            {exercise.notes && (
                                <p className="callout-panel exercise-notes">{exercise.notes}</p>
                            )}

                            <div className="log-section">
                                <p className="kicker log-heading">
                                    Log your sets
                                    {tracking !== 'weight' && (
                                        <span className="muted"> · {exercise.trackingLabel || tracking}</span>
                                    )}
                                </p>
                                <div className="log-grid">
                                    {Array.from({ length: rows }).map((_, index) => {
                                        const setNumber = index + 1;
                                        const row = performances[exercise.id]?.[setNumber];
                                        const isExtra = setNumber > Number(exercise.sets);
                                        const target = setTarget(exercise, setNumber);

                                        return (
                                            <div
                                                className={`set-row${isExtra ? ' set-row-extra' : ''}`}
                                                key={setNumber}
                                            >
                                                <span className="set-label mono">
                                                    {isExtra ? '+' : setNumber}
                                                </span>
                                                {/* The prescribed target for *this*
                                                    set. A wave gives each set its own
                                                    number, invisible when reps was a
                                                    single integer. */}
                                                {target && (
                                                    <span
                                                        className="set-target mono"
                                                        aria-label={`Set ${setNumber} target ${target}`}
                                                    >
                                                        {target}
                                                    </span>
                                                )}
                                                {/* Which inputs a row offers follows
                                                    the tracking type the SERVER
                                                    resolved. A plank asks for
                                                    seconds; asking for kilograms
                                                    is the failure this prevents. */}
                                                {fields.load && (
                                                    <input
                                                        type="number"
                                                        inputMode="decimal"
                                                        step="any"
                                                        className="field field-compact"
                                                        placeholder="body"
                                                        aria-label={loadInputAriaLabel(exercise.exerciseName, setNumber, unit)}
                                                        value={row?.weight ?? ''}
                                                        onChange={(e) =>
                                                            handleSetLog(exercise.id, setNumber, 'weight', e.target.value)
                                                        }
                                                    />
                                                )}
                                                {fields.load && fields.reps && (
                                                    <span className="set-times" aria-hidden="true">×</span>
                                                )}
                                                {fields.reps && (
                                                    <input
                                                        type="number"
                                                        inputMode="numeric"
                                                        className="field field-compact"
                                                        placeholder="reps"
                                                        aria-label={`${exercise.exerciseName} set ${setNumber} reps performed`}
                                                        value={row?.reps ?? ''}
                                                        onChange={(e) =>
                                                            handleSetLog(exercise.id, setNumber, 'reps', e.target.value)
                                                        }
                                                    />
                                                )}
                                                {fields.duration && (
                                                    <>
                                                        <input
                                                            type="number"
                                                            inputMode="numeric"
                                                            className="field field-compact"
                                                            placeholder="sec"
                                                            aria-label={`${exercise.exerciseName} set ${setNumber} seconds held`}
                                                            value={row?.duration ?? ''}
                                                            onChange={(e) =>
                                                                handleSetLog(exercise.id, setNumber, 'duration', e.target.value)
                                                            }
                                                        />
                                                        <span className="set-measure-unit">sec</span>
                                                    </>
                                                )}
                                                {fields.distance && (
                                                    <>
                                                        <input
                                                            type="number"
                                                            inputMode="decimal"
                                                            step="any"
                                                            className="field field-compact"
                                                            placeholder="m"
                                                            aria-label={`${exercise.exerciseName} set ${setNumber} metres covered`}
                                                            value={row?.distance ?? ''}
                                                            onChange={(e) =>
                                                                handleSetLog(exercise.id, setNumber, 'distance', e.target.value)
                                                            }
                                                        />
                                                        <span className="set-measure-unit">m</span>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {history && Object.keys(history).length > 0 && (
                                <div className="history-section">
                                    <p className="kicker log-heading">Previous sessions</p>
                                    <div className="history-list">
                                        {Object.entries(history)
                                            .sort(([a], [b]) => b.localeCompare(a))
                                            .map(([date, sets]) => (
                                                <div key={date} className="embed-shell history-day">
                                                    <div className="history-day-head">
                                                        <span className="history-date">
                                                            {formatSessionDate(date)}
                                                        </span>
                                                        <button
                                                            className="icon-button"
                                                            onClick={() =>
                                                                handleDeleteDatePerformances(exercise.id, date, sets)
                                                            }
                                                            aria-label={`Delete all sets from ${formatSessionDate(date)}`}
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                    <ul className="history-sets">
                                                        {sets.map((perf) => (
                                                            <li key={perf.id} className="history-set">
                                                                <span className="meta-copy">{perf.setNumber}</span>
                                                                {/* A timed set shows its
                                                                    seconds. Rendered as a
                                                                    load it reads
                                                                    "Bodyweight × 0", which
                                                                    looks like a failed lift. */}
                                                                {perf.durationSeconds != null || perf.distanceMetres != null ? (
                                                                    <span className="mono history-load">
                                                                        {describeLoggedSet(perf, perf.unit ?? unit)}
                                                                    </span>
                                                                ) : (
                                                                    <>
                                                                        <span className="mono history-load">
                                                                            {formatLoad(perf.weight, perf.unit ?? unit)}
                                                                        </span>
                                                                        <span className="mono history-reps">
                                                                            <span aria-hidden="true">×</span>
                                                                            {perf.repsPerformed}
                                                                            <span className="sr-only"> reps</span>
                                                                        </span>
                                                                    </>
                                                                )}
                                                                <button
                                                                    className="icon-button"
                                                                    onClick={() =>
                                                                        handleDeletePerformance(exercise.id, perf.id)
                                                                    }
                                                                    aria-label={`Delete set ${perf.setNumber}`}
                                                                >
                                                                    ✕
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}
                        </article>
                    );
                })}
            </div>

            {demo && (
                <ExerciseVideoModal
                    videoUrl={demo.url}
                    title={demo.name}
                    onClose={() => setDemo(null)}
                />
            )}
        </div>
    );
};

export default Train;
